import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import Button_mui from '@mui/material/Button';
import Typography from '@material-ui/core/Typography';
import dayjs from 'dayjs';

import OKCancelDialog from '../../../atoms/Dialog/OKCancelDialog';
import { TableBaseStyle } from './../PartialDischargeStyle';
import { V_PARTIAL_DISCHARGE_STATUS_ITEM } from './../PartialDischarge';

type PartialDischargeGridRowProps = {
    indexNo: number
    partialDischargeType: V_PARTIAL_DISCHARGE_STATUS_ITEM;
    partialDischargeTypeList: V_PARTIAL_DISCHARGE_STATUS_ITEM[];
    onChangeData: (value: V_PARTIAL_DISCHARGE_STATUS_ITEM) => void;
};

const PartialDischargeGridRow: React.FC<PartialDischargeGridRowProps> = (props: PartialDischargeGridRowProps) => {
    const tableStyle = TableBaseStyle();
    const { t } = useTranslation();

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [showMessage, setShowMessage] = React.useState<string>('');

    /**
     * データ更新通知
     */
    useEffect(() => {
        //データ初期値にセットされている内容を再描画時に変えたい場合、useEffectが必要
        console.debug(['PartialDischargeGridRow - useEffect props.PartialDischargeGridRow', props.partialDischargeType]);
        // console.debug(["partialDischargeSetting", partialDischargeSetting]);
    }, [props.partialDischargeType]);

    /**
     * 上位にデータ変更通知
     * @param editRow 
     * @param editFlg 
     */
    const onChangeData = (editRow: V_PARTIAL_DISCHARGE_STATUS_ITEM, editFlg: boolean) => {
        if (editFlg) {
            editRow.MAN_COLDATE = new Date;
            props.onChangeData(editRow);
        }
    }

    /**
     * 手動収集の状態一覧
     * @param sate 
     * @returns 
     */
    const getManualColectionString = (sate?: number): string => {
        console.debug(['getManualColectionString', sate]);
        if (sate == undefined) return t('PartialDischargeGridRow.不明', '不明');
        switch (sate) {
            case 0:
                return t('PartialDischargeGridRow.収集中', '収集中');
            case 1:
                return t('PartialDischargeGridRow.要求中', '要求中');
            case 2:
                return t('PartialDischargeGridRow.開始', '開始');
            case 3:
                return t('PartialDischargeGridRow.開始', '開始');
            default:
                return t('PartialDischargeGridRow.不明', '不明');
        }
    };
    /**
     * 手動収集 ラベルカラー
     * @param sate 
     * @returns 
     */
    const getManualColectionColor = (sate?: number): string => {

        if (sate == undefined) return 'LightGray';
        switch (sate) {
            case 0: //収集中
                return 'LightSkyBlue';
            case 1: //要求中
                return 'Orange';
            case 2: //開始(ラベル)
                return "LightGray";
            case 3: //開始(ボタン)
                return "Green";
            default: //不明
                return 'LightGray';
        }
    };

    /**
     * 状態一覧
     * @param sate 
     * @returns 
     */
    const GetStatusString = (sate?: number): string => {
        if (sate == undefined) return t('PartialDischargeGridRow.不明', '不明');

        switch (sate) {
            case 1:
                return t('PartialDischargeGridRow.軽故障', '軽故障');
            case 2:
                return t('PartialDischargeGridRow.重故障', '重故障');
            case 4:
                return t('PartialDischargeGridRow.システム故障', 'システム故障');
            case 0:
                return t('PartialDischargeGridRow.正常', '正常');
            default:
                return t('PartialDischargeGridRow.不明', '不明');
        }
    }

    /**
     * 状態表示を取得
     * @param sate 
     * @returns 
     */
    const GetStatusColor = (sate?: number): { color: string; background: string } => {
        if (sate == undefined) return { color: 'white', background: 'LightGray' };

        switch (sate) {
            case 1: //軽故障
                return { color: 'black', background: 'yellow' };
            case 2: //重故障
                return { color: 'white', background: 'red' };
            case 4: //システム故障
                return { color: 'white', background: 'red' };
            case 0:
                //正常
                return { color: 'white', background: 'green' };
            default:
                return { color: 'white', background: 'LightGray' };
        }
    };

    /**
   *　OK処理
   */
    const handleDialogOkButton = () => {
        props.partialDischargeType.manualButtonStat = 1; //要求中に変更
        onChangeData(props.partialDischargeType, true);
        setDialogOpen(false);
    };
    /**
   * 確認ダイアログ
   */
    const handleCheckDialogShow = () => {
        setShowMessage(t('PartialDischargeGridRow.手動収集を要求しますか', '手動収集を要求しますか？'));
        setDialogOpen(true);
    };

    return (
        <>
            <TableRow key={props.indexNo}>
                {/** No. */}
                <TableCell className={tableStyle.bodyCell} style={{ width: '40px' }}>{props.indexNo + 1}</TableCell>
                {/** 機器番号機器名称 */}
                <TableCell className={tableStyle.bodyCell_left} style={{ width: '240px' }}>
                    {props.partialDischargeType.Dev_NoName}
                </TableCell>
                {/** 状態 */}
                <TableCell className={tableStyle.bodyCell} style={{ width: '200px' }}>
                    {props.partialDischargeType.Dev_NoName !== " " ? //機器番号が登録されていない場合非表示
                        <div
                            style={{
                                ...GetStatusColor(props.partialDischargeType.StatNo),
                                padding: '7px',
                            }}
                        >
                            {GetStatusString(props.partialDischargeType.StatNo)}
                        </div>
                        : null
                    }
                </TableCell>
                {/** 最新収集時刻 */}
                <TableCell className={tableStyle.bodyCell} style={{ width: '200px' }}>
                    {props.partialDischargeType.Coldate !== undefined && props.partialDischargeType.Coldate !== null ?
                        dayjs(props.partialDischargeType.Coldate).format('YYYY-MM-DD HH:mm:ss') : ""
                    }
                </TableCell>
                {/** 手動収集 */}
                <TableCell className={tableStyle.bodyCell} style={{ width: '140px' }}>
                    {props.partialDischargeType.Dev_NoName !== " " ? //機器番号が登録されていない場合非表示
                        props.partialDischargeType.manualButtonStat == 3 ? (
                            <Button_mui
                                className={tableStyle.manualButton}
                                fullWidth={true}
                                variant="contained"
                                style={{
                                    backgroundColor: getManualColectionColor(props.partialDischargeType.manualButtonStat),
                                    color: "white",
                                }}
                                onClick={() => {
                                    handleCheckDialogShow();

                                }}
                                disabled={props.partialDischargeType.manualButtonStat != 3}
                            >
                                {getManualColectionString(props.partialDischargeType.manualButtonStat)}
                            </Button_mui>
                        ) : (
                            <div
                                style={{
                                    background: getManualColectionColor(props.partialDischargeType.manualButtonStat),
                                    color: 'white',
                                    padding: '7px',
                                }}
                            >
                                {getManualColectionString(props.partialDischargeType.manualButtonStat)}
                            </div>
                        )
                        : null
                    }
                    <OKCancelDialog
                        open={dialogOpen}
                        onOK={() => handleDialogOkButton()}
                        onCancel={() => setDialogOpen(false)}
                        title={t('PartialDischargeGridRow.確認', '確認')}
                        okText={t('PartialDischargeGridRow.OK', 'OK')}
                        cancelText={t('PartialDischargeGridRow.キャンセル', 'キャンセル')}
                        maxWidth={'sm'}
                    >
                        <Typography>{showMessage}</Typography>
                    </OKCancelDialog>
                </TableCell>
                {/** 次回収集予定 */}
                <TableCell className={tableStyle.bodyCell} style={{ width: '200px' }}>
                    {props.partialDischargeType.AUTO_NEXTDATE !== undefined && props.partialDischargeType.AUTO_NEXTDATE !== null ?
                        dayjs(props.partialDischargeType.AUTO_NEXTDATE).format('YYYY-MM-DD HH:mm:ss') : ""
                    }
                </TableCell>
            </TableRow>
        </>
    )
}
export default PartialDischargeGridRow;